import {
	FxIdDomainEntitiesPlayGameType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesPlayOtaGameVersion,
	FxGamesLibWebPublicDataBase,
	FxIdWebFeaturesProfileGetResponseFeature
} from "../../../Api/gen";
import React, { useState } from "react";
import { IframeLoader, UnityLoader } from "./Loaders";
import { GameLoading } from "./GameLoading";
import GameOrientation from "./GameOrientation";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useProfileDataQuery } from "../../../Api/Queries";
import { webPlayerStore } from "../store/webPlayerStore";
import OpenApiClient from "@app/Api/OpenApiClient";

interface IGameLoaderProps {
	gameConfig: FxGamesLibWebPublicDataBase;
	gameVersion?: FxIdWebFeaturesPlayOtaGameVersion | null;
	buildNumber?: number | null;
	onGameLoaded?: () => void;
	currentAccountId: number;
	dimensions: { width: number; height: number };
}

// const MemoizedLegacyUnityLoader = React.memo(LegacyUnityLoader);
const MemoizedUnityLoader = React.memo(UnityLoader);
const MemoizedIFrameLoader = React.memo(IframeLoader);

export const GameLoader = (props: IGameLoaderProps) => {
	log.info("Render GameLoader...");

	const { gameVersion, buildNumber, onGameLoaded, currentAccountId } = props;
	const { data: profileData } = useProfileDataQuery();
	const adjustedGameVersion =
		(profileData?.Features.includes(FxIdWebFeaturesProfileGetResponseFeature.ListGames)
			? gameVersion
			: undefined) ?? FxIdWebFeaturesPlayOtaGameVersion.Unspecified;
	const adjustedBuildNumber = buildNumber ?? 0;
	const [searchParams, setSearchParams] = useSearchParams();

	const gameConfig = webPlayerStore((state) => state.gameConfig);

	const [progressLoading, setProgressLoading] = useState(0);

	const { data: buildConfig } = useQuery(
		[
			"build-config",
			gameConfig.GameSystemName,
			adjustedGameVersion,
			adjustedBuildNumber,
			currentAccountId
		] as const,
		{
			queryFn: async (ctx) => {
				const [, gameId, version, buildNum] = ctx.queryKey;
				const config = await OpenApiClient.Play.fxIdWebFeaturesPlayGameEndpoint(
					gameId,
					version,
					buildNum,
					FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId
				);
				const additionalSearchParams = config.LaunchData?.AppendToQuery;
				if (additionalSearchParams) {
					const updatedParams = {
						...Object.fromEntries(searchParams),
						...Object.fromEntries(new URLSearchParams(additionalSearchParams))
					};
					setSearchParams(updatedParams);
				}
				return config;
			},

			onSuccess: (config) => log.info("Loading game with config %o", config),
			staleTime: Infinity
		}
	);

	if (!buildConfig) return null;

	return (
		<>
			{buildConfig.PlayGameType === FxIdDomainEntitiesPlayGameType.Iframe ? (
				<MemoizedIFrameLoader
					buildConfig={buildConfig}
					onGameLoaded={onGameLoaded}
					onGameLoadingProgress={setProgressLoading}
					currentAccountId={props.currentAccountId}
					dimensions={props.dimensions}
				/>
			) : (
				<>
					<GameLoading progress={progressLoading} />
					<MemoizedUnityLoader
						buildConfig={buildConfig}
						onGameLoaded={onGameLoaded}
						onGameLoadingProgress={setProgressLoading}
						currentAccountId={props.currentAccountId}
						dimensions={props.dimensions}
					/>
				</>
			)}
			<GameOrientation gameOrientation={buildConfig.GameOrientation} />
		</>
	);
};
